import { useRef, useState } from 'react';

/**
 * Drag-resizable table columns (改改 6/26). Pair with the `.rz` table class (table-layout:fixed +
 * cell clipping) and a <colgroup>. Usage:
 *   const rz = useResizableColumns((id) => DEFAULTS[id] ?? 100);
 *   <table className="… rz" style={{ width: rz.totalWidth(colIds) }}>
 *     <colgroup>{colIds.map((id) => <col key={id} style={{ width: rz.wOf(id) }} />)}</colgroup>
 *     <th>序号{rz.handle('seq')}</th> …
 *   {rz.resized && <button onClick={rz.reset}>重置列宽</button>}
 */
export function useResizableColumns(defaultWidth: (id: string) => number) {
  const [colW, setColW] = useState<Record<string, number>>({});
  const drag = useRef<{ id: string; x: number; w: number } | null>(null);
  const wOf = (id: string) => colW[id] ?? defaultWidth(id);
  function startResize(ev: React.MouseEvent, id: string) {
    ev.preventDefault(); ev.stopPropagation();
    drag.current = { id, x: ev.clientX, w: wOf(id) };
    document.body.style.userSelect = 'none';
    const onMove = (e: MouseEvent) => { if (!drag.current) return; const w = Math.max(36, drag.current.w + (e.clientX - drag.current.x)); setColW((c) => ({ ...c, [drag.current!.id]: w })); };
    const onUp = () => { drag.current = null; document.body.style.userSelect = ''; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  }
  /** A drag grip to drop at the end of a <th>; resizes the column with the matching <col> id. */
  const handle = (id: string) => (
    <span className="col-resizer" title="拖动调整列宽 / drag to resize" onMouseDown={(ev) => startResize(ev, id)} onClick={(ev) => ev.stopPropagation()} />
  );
  return {
    wOf,
    handle,
    totalWidth: (ids: string[]) => ids.reduce((s, id) => s + wOf(id), 0),
    reset: () => setColW({}),
    resized: Object.keys(colW).length > 0,
  };
}
